import React, { useState, useMemo, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Select,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  App,
  Modal,
  Form,
  InputNumber,
  Input,
  DatePicker,
  Descriptions,
  Divider,
  Empty,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
  MinusCircleOutlined,
  AlertOutlined,
  FilePdfOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useGetPurchaseOrdersQuery,
  useCreatePurchaseOrderMutation,
  useUpdatePurchaseOrderMutation,
  useDeletePurchaseOrderMutation,
  useReceivePurchaseOrderMutation,
  useLazyGetLowStockSuggestionsQuery,
  useLazyGetPurchaseOrderPdfQuery,
} from '../../services/purchaseOrdersApi';
import { useGetSuppliersQuery, useGetSupplierItemsQuery } from '../../services/suppliersApi';
import { useGetInventoryItemsQuery } from '../../services/inventoryApi';
import {
  PurchaseOrder,
  PurchaseOrderItem,
  PurchaseOrderStatus,
  CreatePurchaseOrderRequest,
  UpdatePurchaseOrderRequest,
  ReceivePurchaseOrderRequest,
  SuggestedPurchaseOrderItem,
} from '../../types/inventory';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { formatDateOnly, parseDateOnly } from '../../utils/dateHelpers';
import { getErrorMessage } from '../../utils/errorHelpers';
import { formatStock } from '../../utils/unitHelpers';

const { Option } = Select;
const { Text, Title } = Typography;
const { TextArea } = Input;

const TAX_RATE = 0.16;

interface SuggestionItem extends SuggestedPurchaseOrderItem {
  suggestedSupplierId: string | null;
  suggestedSupplierName: string | null;
  selectedSupplierId: string | null;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(amount);
};

const statusLabels: Record<PurchaseOrderStatus, string> = {
  Draft: 'Borrador',
  Sent: 'Enviada',
  PartiallyReceived: 'Parcialmente Recibida',
  Received: 'Recibida',
  Cancelled: 'Cancelada',
};

const statusColors: Record<PurchaseOrderStatus, string> = {
  Draft: 'default',
  Sent: 'blue',
  PartiallyReceived: 'orange',
  Received: 'green',
  Cancelled: 'red',
};

interface OrderFormItem {
  inventoryItemId: string;
  quantity: number;
  unitPrice: number;
  notes?: string;
}

interface ReceiveFormItem {
  purchaseOrderItemId: string;
  itemName: string;
  orderedQty: number;
  alreadyReceived: number;
  quantityToReceive: number;
}

const PurchaseOrdersTab: React.FC = () => {
  const { isMobile } = useResponsive();
  const { message, modal } = App.useApp();
  const { formatInTimeZone, getLocalDate } = useRestaurantTimeZone();

  // Filter state
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | 'all'>('all');

  // Modal states
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<PurchaseOrder | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingOrder, setViewingOrder] = useState<PurchaseOrder | null>(null);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [receivingOrder, setReceivingOrder] = useState<PurchaseOrder | null>(null);

  // Form state
  const [form] = Form.useForm();
  const [orderItems, setOrderItems] = useState<OrderFormItem[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | undefined>(undefined);

  // Receive form state
  const [receiveItems, setReceiveItems] = useState<ReceiveFormItem[]>([]);
  const [receiveNotes, setReceiveNotes] = useState('');

  // Low stock suggestions modal state
  const [suggestionsModalOpen, setSuggestionsModalOpen] = useState(false);
  const [suggestionItems, setSuggestionItems] = useState<SuggestionItem[]>([]);
  const [creatingFromSuggestions, setCreatingFromSuggestions] = useState(false);

  // API hooks
  const { data: orders, isLoading, refetch } = useGetPurchaseOrdersQuery();
  const { data: suppliers } = useGetSuppliersQuery();
  const { data: inventoryItems } = useGetInventoryItemsQuery();
  const { data: supplierItems } = useGetSupplierItemsQuery(selectedSupplierId!, {
    skip: !selectedSupplierId,
  });

  const [createOrder, { isLoading: creating }] = useCreatePurchaseOrderMutation();
  const [updateOrder, { isLoading: updating }] = useUpdatePurchaseOrderMutation();
  const [deleteOrder] = useDeletePurchaseOrderMutation();
  const [receiveOrder, { isLoading: receiving }] = useReceivePurchaseOrderMutation();
  const [triggerGetSuggestions, { isFetching: fetchingSuggestions }] = useLazyGetLowStockSuggestionsQuery();
  const [triggerGetPdf] = useLazyGetPurchaseOrderPdfQuery();

  // Filtered orders
  const filteredOrders = useMemo(() => {
    if (!orders) return [];
    if (statusFilter === 'all') return orders;
    return orders.filter((o) => o.status === statusFilter);
  }, [orders, statusFilter]);

  // Computed totals for form
  const formSubtotal = useMemo(() => {
    return orderItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  }, [orderItems]);

  const formTax = formSubtotal * TAX_RATE;
  const formTotal = formSubtotal + formTax;

  // Auto-fill price from supplier items
  const getSupplierPrice = (inventoryItemId: string): number | undefined => {
    if (!supplierItems) return undefined;
    const match = supplierItems.find((si) => si.inventoryItemId === inventoryItemId);
    return match?.supplierPrice;
  };

  // ==================== Handlers ====================

  const handleOpenCreate = () => {
    setEditingOrder(null);
    setOrderItems([]);
    setSelectedSupplierId(undefined);
    form.resetFields();
    form.setFieldsValue({ orderDate: dayjs() });
    setFormModalOpen(true);
  };

  const handleOpenEdit = (order: PurchaseOrder) => {
    setEditingOrder(order);
    setSelectedSupplierId(order.supplierId ?? undefined);
    setOrderItems(
      order.items.map((item) => ({
        inventoryItemId: item.inventoryItemId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        notes: item.notes,
      }))
    );
    form.setFieldsValue({
      supplierId: order.supplierId ?? undefined,
      orderDate: parseDateOnly(order.orderDate),
      expectedDeliveryDate: order.expectedDeliveryDate
        ? parseDateOnly(order.expectedDeliveryDate)
        : undefined,
      notes: order.notes,
    });
    setFormModalOpen(true);
  };

  const handleCloseForm = () => {
    setFormModalOpen(false);
    setEditingOrder(null);
    setOrderItems([]);
    setSelectedSupplierId(undefined);
    form.resetFields();
  };

  const handleOpenView = (order: PurchaseOrder) => {
    setViewingOrder(order);
    setViewModalOpen(true);
  };

  const handleCloseView = () => {
    setViewModalOpen(false);
    setViewingOrder(null);
  };

  const handleOpenReceive = (order: PurchaseOrder) => {
    setReceivingOrder(order);
    setReceiveItems(
      order.items.map((item) => ({
        purchaseOrderItemId: item.id,
        itemName: item.itemName,
        orderedQty: item.quantity,
        alreadyReceived: item.quantityReceived,
        quantityToReceive: 0,
      }))
    );
    setReceiveNotes('');
    setReceiveModalOpen(true);
  };

  const handleCloseReceive = () => {
    setReceiveModalOpen(false);
    setReceivingOrder(null);
    setReceiveItems([]);
    setReceiveNotes('');
  };

  const handleSubmitForm = async () => {
    try {
      const values = await form.validateFields();

      if (orderItems.length === 0) {
        message.warning('Agrega al menos un artículo a la orden');
        return;
      }

      const hasInvalidItems = orderItems.some(
        (item) => !item.inventoryItemId || item.quantity <= 0 || item.unitPrice < 0
      );
      if (hasInvalidItems) {
        message.warning('Verifica que todos los artículos tengan datos válidos');
        return;
      }

      const payload = {
        supplierId: values.supplierId || null,
        notes: values.notes || undefined,
        orderDate: values.orderDate.format('YYYY-MM-DD'),
        expectedDeliveryDate: values.expectedDeliveryDate
          ? values.expectedDeliveryDate.format('YYYY-MM-DD')
          : undefined,
        items: orderItems.map((item) => ({
          inventoryItemId: item.inventoryItemId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          notes: item.notes || undefined,
        })),
      };

      if (editingOrder) {
        await updateOrder({
          id: editingOrder.id,
          data: payload as UpdatePurchaseOrderRequest,
        }).unwrap();
        message.success('Orden de compra actualizada');
      } else {
        await createOrder(payload as CreatePurchaseOrderRequest).unwrap();
        message.success('Orden de compra creada');
      }

      handleCloseForm();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return; // Form validation error
      }
      message.error(getErrorMessage(error));
    }
  };

  const handleDelete = (order: PurchaseOrder) => {
    modal.confirm({
      title: 'Eliminar orden de compra',
      content: `¿Estás seguro de eliminar la orden #${order.orderNumber}? Esta acción no se puede deshacer.`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteOrder(order.id).unwrap();
          message.success('Orden de compra eliminada');
        } catch (error) {
          message.error(getErrorMessage(error));
        }
      },
    });
  };

  const handleSubmitReceive = async () => {
    if (!receivingOrder) return;

    const itemsToReceive = receiveItems.filter((item) => item.quantityToReceive > 0);
    if (itemsToReceive.length === 0) {
      message.warning('Ingresa la cantidad recibida en al menos un artículo');
      return;
    }

    const payload: ReceivePurchaseOrderRequest = {
      items: itemsToReceive.map((item) => ({
        purchaseOrderItemId: item.purchaseOrderItemId,
        quantityReceived: item.quantityToReceive,
      })),
      notes: receiveNotes || undefined,
    };

    try {
      await receiveOrder({ id: receivingOrder.id, data: payload }).unwrap();
      message.success('Recepción registrada exitosamente');
      handleCloseReceive();
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  };

  // ==================== Low Stock Suggestions ====================

  const handleOpenSuggestions = async () => {
    try {
      const result = await triggerGetSuggestions().unwrap();
      // Flatten grouped response into a flat list with per-item supplier info
      const flatItems: SuggestionItem[] = result.suggestedOrders.flatMap(group =>
        group.items.map(item => ({
          ...item,
          suggestedSupplierId: group.supplierId,
          suggestedSupplierName: group.supplierName,
          selectedSupplierId: null, // Default: no supplier (lista de compras)
        }))
      );
      setSuggestionItems(flatItems);
      setSuggestionsModalOpen(true);
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  };

  const handleCloseSuggestions = () => {
    setSuggestionsModalOpen(false);
    setSuggestionItems([]);
  };

  const handleSuggestionQtyChange = (index: number, value: number | null) => {
    setSuggestionItems(prev => prev.map((item, i) =>
      i === index ? { ...item, suggestedQuantity: value ?? 0 } : item
    ));
  };

  const handleSuggestionSupplierChange = (index: number, supplierId: string | null) => {
    setSuggestionItems(prev => prev.map((item, i) =>
      i === index ? { ...item, selectedSupplierId: supplierId } : item
    ));
  };

  const handleApplySuggestedSuppliers = () => {
    setSuggestionItems(prev => prev.map(item => ({
      ...item,
      selectedSupplierId: item.suggestedSupplierId,
    })));
  };

  const handleClearAllSuppliers = () => {
    setSuggestionItems(prev => prev.map(item => ({
      ...item,
      selectedSupplierId: null,
    })));
  };

  const handleRemoveSuggestionItem = (index: number) => {
    setSuggestionItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateFromSuggestions = async () => {
    const validItems = suggestionItems.filter(i => i.suggestedQuantity > 0);
    if (validItems.length === 0) {
      message.warning('No hay artículos válidos para crear órdenes');
      return;
    }

    // Group items by selectedSupplierId to create one PO per supplier group
    const groups = new Map<string, { supplierId: string | null; items: SuggestionItem[] }>();
    for (const item of validItems) {
      const key = item.selectedSupplierId ?? '__no_supplier__';
      if (!groups.has(key)) {
        groups.set(key, { supplierId: item.selectedSupplierId, items: [] });
      }
      groups.get(key)!.items.push(item);
    }

    setCreatingFromSuggestions(true);
    try {
      const results = await Promise.allSettled(
        Array.from(groups.values()).map(group =>
          createOrder({
            supplierId: group.supplierId,
            orderDate: getLocalDate('YYYY-MM-DD'),
            notes: 'Generada automáticamente por stock bajo',
            items: group.items.map(item => ({
              inventoryItemId: item.inventoryItemId,
              quantity: item.suggestedQuantity,
              unitPrice: item.unitPrice,
            })),
          } as CreatePurchaseOrderRequest).unwrap()
        )
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      if (failed > 0) {
        message.warning(`Se crearon ${succeeded} de ${succeeded + failed} órdenes`);
      } else {
        message.success(`Se ${succeeded === 1 ? 'creó 1 orden' : `crearon ${succeeded} órdenes`} de compra`);
      }
      handleCloseSuggestions();
    } catch (error) {
      message.error(getErrorMessage(error));
    } finally {
      setCreatingFromSuggestions(false);
    }
  };

  // ==================== PDF Download ====================

  const handleDownloadPdf = async (order: PurchaseOrder) => {
    try {
      message.loading({ content: 'Generando PDF...', key: 'pdf-download' });
      const result = await triggerGetPdf(order.id).unwrap();
      const url = URL.createObjectURL(result);
      const link = document.createElement('a');
      link.href = url;
      link.download = `OrdenCompra_${order.orderNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success({ content: 'PDF descargado', key: 'pdf-download' });
    } catch {
      message.error({ content: 'Error al generar el PDF', key: 'pdf-download' });
    }
  };

  // ==================== Order Items Management ====================

  const handleAddItem = () => {
    setOrderItems([
      ...orderItems,
      { inventoryItemId: '', quantity: 1, unitPrice: 0, notes: undefined },
    ]);
  };

  const handleRemoveItem = (index: number) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const handleItemChange = (
    index: number,
    field: keyof OrderFormItem,
    value: string | number | undefined
  ) => {
    const updated = [...orderItems];
    (updated[index] as any)[field] = value;

    // Auto-fill price when selecting an item
    if (field === 'inventoryItemId' && typeof value === 'string') {
      const supplierPrice = getSupplierPrice(value);
      if (supplierPrice !== undefined) {
        updated[index].unitPrice = supplierPrice;
      }
    }

    setOrderItems(updated);
  };

  // ==================== Supplier change: reset items prices ====================

  useEffect(() => {
    if (supplierItems && orderItems.length > 0) {
      const updated = orderItems.map((item) => {
        if (item.inventoryItemId) {
          const supplierPrice = supplierItems.find(
            (si) => si.inventoryItemId === item.inventoryItemId
          )?.supplierPrice;
          if (supplierPrice !== undefined) {
            return { ...item, unitPrice: supplierPrice };
          }
        }
        return item;
      });
      setOrderItems(updated);
    }
    // Only run when supplierItems data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierItems]);

  // ==================== Table Columns ====================

  const columns: ColumnsType<PurchaseOrder> = [
    {
      title: '# Orden',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 90,
      render: (num: number) => <Text strong>#{num}</Text>,
    },
    {
      title: 'Proveedor',
      dataIndex: 'supplierName',
      key: 'supplierName',
      ellipsis: true,
      render: (name: string | null) => name || <Text type="secondary" italic>Sin proveedor</Text>,
    },
    {
      title: 'Fecha',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 120,
      render: (date: string) => formatDateOnly(date, 'DD/MM/YYYY'),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status: PurchaseOrderStatus) => (
        <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>
      ),
    },
    {
      title: 'Artículos',
      key: 'itemsCount',
      width: 90,
      align: 'center',
      render: (_: unknown, record: PurchaseOrder) => record.items.length,
    },
    {
      title: 'Total',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 130,
      align: 'right',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 210,
      align: 'center',
      render: (_: unknown, record: PurchaseOrder) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleOpenView(record)}
          />
          <Button
            type="text"
            size="small"
            icon={<FilePdfOutlined />}
            onClick={() => handleDownloadPdf(record)}
            title="Descargar PDF"
          />
          {record.status === 'Draft' && (
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenEdit(record)}
            />
          )}
          {(record.status === 'Sent' || record.status === 'PartiallyReceived') && (
            <Button
              type="text"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleOpenReceive(record)}
              style={{ color: '#52c41a' }}
            />
          )}
          {record.status === 'Draft' && (
            <Popconfirm
              title="¿Eliminar esta orden?"
              onConfirm={() => handleDelete(record)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="text" size="small" icon={<DeleteOutlined />} danger />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // ==================== Mobile Card ====================

  const renderMobileCard = (order: PurchaseOrder) => (
    <Card
      key={order.id}
      size="small"
      style={{ marginBottom: 12 }}
      actions={[
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleOpenView(order)}
        >
          Ver
        </Button>,
        <Button
          type="text"
          size="small"
          icon={<FilePdfOutlined />}
          onClick={() => handleDownloadPdf(order)}
        >
          PDF
        </Button>,
        ...(order.status === 'Draft'
          ? [
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleOpenEdit(order)}
              >
                Editar
              </Button>,
            ]
          : []),
        ...(order.status === 'Sent' || order.status === 'PartiallyReceived'
          ? [
              <Button
                type="text"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleOpenReceive(order)}
                style={{ color: '#52c41a' }}
              >
                Recibir
              </Button>,
            ]
          : []),
        ...(order.status === 'Draft'
          ? [
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDelete(order)}
              >
                Eliminar
              </Button>,
            ]
          : []),
      ]}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Text strong>#{order.orderNumber}</Text>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{order.supplierName || <i>Sin proveedor</i>}</div>
          <div style={{ fontSize: 11, color: 'var(--color-neutral-text)' }}>
            {formatDateOnly(order.orderDate, 'DD/MM/YYYY')}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Tag color={statusColors[order.status]}>{statusLabels[order.status]}</Tag>
          <div style={{ fontWeight: 600, marginTop: 4 }}>{formatCurrency(order.totalAmount)}</div>
          <div style={{ fontSize: 11, color: 'var(--color-neutral-text)' }}>
            {order.items.length} artículo{order.items.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </Card>
  );

  // ==================== View Details Modal Content ====================

  const viewItemsColumns: ColumnsType<PurchaseOrderItem> = [
    {
      title: 'Artículo',
      dataIndex: 'itemName',
      key: 'itemName',
      ellipsis: true,
    },
    {
      title: 'Unidad',
      dataIndex: 'itemUnit',
      key: 'itemUnit',
      width: 80,
    },
    {
      title: 'Cant. Ordenada',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 110,
      align: 'center',
    },
    {
      title: 'Cant. Recibida',
      dataIndex: 'quantityReceived',
      key: 'quantityReceived',
      width: 110,
      align: 'center',
      render: (received: number, record: PurchaseOrderItem) => (
        <Text
          style={{
            color: received >= record.quantity ? '#52c41a' : received > 0 ? '#faad14' : undefined,
          }}
        >
          {received}
        </Text>
      ),
    },
    {
      title: 'Precio Unit.',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 110,
      align: 'right',
      render: (price: number) => formatCurrency(price),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 110,
      align: 'right',
      render: (total: number) => formatCurrency(total),
    },
  ];

  // ==================== Receive Modal Columns ====================

  const receiveColumns: ColumnsType<ReceiveFormItem> = [
    {
      title: 'Artículo',
      dataIndex: 'itemName',
      key: 'itemName',
      ellipsis: true,
    },
    {
      title: 'Ordenado',
      dataIndex: 'orderedQty',
      key: 'orderedQty',
      width: 90,
      align: 'center',
    },
    {
      title: 'Recibido',
      dataIndex: 'alreadyReceived',
      key: 'alreadyReceived',
      width: 90,
      align: 'center',
    },
    {
      title: 'Cant. a Recibir',
      key: 'quantityToReceive',
      width: 130,
      align: 'center',
      render: (_: unknown, record: ReceiveFormItem, index: number) => (
        <InputNumber
          min={0}
          max={record.orderedQty - record.alreadyReceived}
          value={record.quantityToReceive}
          onChange={(value) => {
            const updated = [...receiveItems];
            updated[index] = { ...updated[index], quantityToReceive: value ?? 0 };
            setReceiveItems(updated);
          }}
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
  ];

  // ==================== Render ====================

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? 12 : 16,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
          Órdenes de Compra
        </Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            size={isMobile ? 'small' : 'middle'}
          >
            {!isMobile && 'Actualizar'}
          </Button>
          <Button
            icon={<AlertOutlined />}
            onClick={handleOpenSuggestions}
            loading={fetchingSuggestions}
            size={isMobile ? 'small' : 'middle'}
            style={{ color: '#faad14', borderColor: '#faad14' }}
          >
            {isMobile ? 'Stock Bajo' : 'Generar por Stock Bajo'}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenCreate}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? 'Nueva' : 'Nueva Orden'}
          </Button>
        </Space>
      </div>

      {/* Status filter */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8} md={6}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            Estado:
          </Text>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: '100%' }}
            size={isMobile ? 'middle' : 'middle'}
          >
            <Option value="all">Todos</Option>
            <Option value="Draft">Borrador</Option>
            <Option value="Sent">Enviada</Option>
            <Option value="PartiallyReceived">Parcialmente Recibida</Option>
            <Option value="Received">Recibida</Option>
            <Option value="Cancelled">Cancelada</Option>
          </Select>
        </Col>
      </Row>

      {/* Table / Mobile Cards */}
      {isMobile ? (
        isLoading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>Cargando...</div>
        ) : filteredOrders.length > 0 ? (
          <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            {filteredOrders.map((order) => renderMobileCard(order))}
          </div>
        ) : (
          <Empty
            description="No hay órdenes de compra"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: 40 }}
          />
        )
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredOrders}
          loading={isLoading}
          size="middle"
          scroll={{ x: 900 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} órdenes`,
          }}
          locale={{
            emptyText: (
              <Empty
                description="No hay órdenes de compra"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      )}

      {/* ==================== Create/Edit Modal ==================== */}
      <Modal
        title={
          <Space>
            <ShoppingCartOutlined />
            <span>{editingOrder ? 'Editar Orden de Compra' : 'Nueva Orden de Compra'}</span>
          </Space>
        }
        open={formModalOpen}
        onCancel={handleCloseForm}
        onOk={handleSubmitForm}
        okText={editingOrder ? 'Guardar Cambios' : 'Crear Orden'}
        cancelText="Cancelar"
        confirmLoading={creating || updating}
        width={isMobile ? '98%' : 800}
        style={{ top: isMobile ? 10 : 20 }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" size={isMobile ? 'middle' : 'middle'}>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="supplierId"
                label="Proveedor"
              >
                <Select
                  placeholder="Sin proveedor (Lista de compras)"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  onChange={(value: string | undefined) => {
                    setSelectedSupplierId(value);
                  }}
                >
                  {suppliers
                    ?.filter((s) => s.isActive)
                    .map((supplier) => (
                      <Option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="orderDate"
                label="Fecha de Orden"
                rules={[{ required: true, message: 'Selecciona la fecha' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="expectedDeliveryDate" label="Fecha Estimada de Entrega">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="notes" label="Notas">
                <TextArea rows={2} placeholder="Notas adicionales (opcional)" />
              </Form.Item>
            </Col>
          </Row>

          {/* Items Section */}
          <Divider orientation="left" style={{ fontSize: 14 }}>
            Artículos
          </Divider>

          {orderItems.map((item, index) => (
            <Row
              key={index}
              gutter={[8, 8]}
              align="middle"
              style={{
                marginBottom: 8,
                padding: '8px',
                background: 'var(--bg-secondary)',
                borderRadius: 6,
              }}
            >
              <Col xs={24} md={8}>
                <Select
                  placeholder="Artículo"
                  value={item.inventoryItemId || undefined}
                  onChange={(value: string) => handleItemChange(index, 'inventoryItemId', value)}
                  showSearch
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                  size="small"
                >
                  {inventoryItems
                    ?.filter((inv) => inv.isActive && !inv.isPreparation)
                    .map((inv) => (
                      <Option key={inv.id} value={inv.id}>
                        {inv.name} ({inv.unit})
                      </Option>
                    ))}
                </Select>
              </Col>
              <Col xs={8} md={4}>
                <InputNumber
                  placeholder="Cantidad"
                  value={item.quantity}
                  onChange={(value) => handleItemChange(index, 'quantity', value ?? 0)}
                  min={0.01}
                  step={1}
                  style={{ width: '100%' }}
                  size="small"
                />
              </Col>
              <Col xs={8} md={4}>
                <InputNumber
                  placeholder="Precio Unit."
                  value={item.unitPrice}
                  onChange={(value) => handleItemChange(index, 'unitPrice', value ?? 0)}
                  min={0}
                  step={1}
                  prefix="$"
                  style={{ width: '100%' }}
                  size="small"
                />
              </Col>
              <Col xs={6} md={4}>
                <Text style={{ fontSize: 13 }}>
                  {formatCurrency(item.quantity * item.unitPrice)}
                </Text>
              </Col>
              <Col xs={2} md={2}>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<MinusCircleOutlined />}
                  onClick={() => handleRemoveItem(index)}
                />
              </Col>
              {!isMobile && (
                <Col xs={24} md={24}>
                  <Input
                    placeholder="Notas del artículo (opcional)"
                    value={item.notes || ''}
                    onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                    size="small"
                    style={{ marginTop: 4 }}
                  />
                </Col>
              )}
            </Row>
          ))}

          <Button
            type="dashed"
            onClick={handleAddItem}
            block
            icon={<PlusOutlined />}
            style={{ marginBottom: 16 }}
          >
            Agregar Artículo
          </Button>

          {/* Totals */}
          {orderItems.length > 0 && (
            <div
              style={{
                padding: 16,
                background: 'var(--bg-success-light)',
                borderRadius: 8,
                border: '1px solid var(--border-success)',
              }}
            >
              <Row justify="end">
                <Col xs={16} md={8} style={{ textAlign: 'right' }}>
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary">Subtotal:</Text>
                    <Text strong style={{ marginLeft: 12 }}>
                      {formatCurrency(formSubtotal)}
                    </Text>
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary">IVA (16%):</Text>
                    <Text strong style={{ marginLeft: 12 }}>
                      {formatCurrency(formTax)}
                    </Text>
                  </div>
                  <Divider style={{ margin: '8px 0' }} />
                  <div>
                    <Text type="secondary">Total:</Text>
                    <Text strong style={{ marginLeft: 12, fontSize: 18, color: 'var(--color-success-text)' }}>
                      {formatCurrency(formTotal)}
                    </Text>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Form>
      </Modal>

      {/* ==================== View Details Modal ==================== */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>Detalle de Orden #{viewingOrder?.orderNumber}</span>
          </Space>
        }
        open={viewModalOpen}
        onCancel={handleCloseView}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              icon={<FilePdfOutlined />}
              onClick={() => viewingOrder && handleDownloadPdf(viewingOrder)}
            >
              Descargar PDF
            </Button>
            <Button onClick={handleCloseView}>Cerrar</Button>
          </div>
        }
        width={isMobile ? '98%' : 800}
        style={{ top: isMobile ? 10 : 20 }}
        destroyOnClose
      >
        {viewingOrder && (
          <div style={{ padding: isMobile ? '8px 0' : '16px 0' }}>
            {/* Order Info */}
            <Descriptions
              bordered
              size="small"
              column={isMobile ? 1 : 2}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="# Orden">
                <Text strong>#{viewingOrder.orderNumber}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Estado">
                <Tag color={statusColors[viewingOrder.status]}>
                  {statusLabels[viewingOrder.status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Proveedor">
                {viewingOrder.supplierName || <Text type="secondary" italic>Sin proveedor</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Orden">
                {formatDateOnly(viewingOrder.orderDate, 'DD/MM/YYYY')}
              </Descriptions.Item>
              {viewingOrder.expectedDeliveryDate && (
                <Descriptions.Item label="Entrega Estimada">
                  {formatDateOnly(viewingOrder.expectedDeliveryDate, 'DD/MM/YYYY')}
                </Descriptions.Item>
              )}
              {viewingOrder.receivedDate && (
                <Descriptions.Item label="Fecha de Recepción">
                  {formatInTimeZone(viewingOrder.receivedDate, 'DD/MM/YYYY')}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Creada por">
                {viewingOrder.createdByUserName}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Creación">
                {formatInTimeZone(viewingOrder.createdAt, 'DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
              {viewingOrder.notes && (
                <Descriptions.Item label="Notas" span={isMobile ? 1 : 2}>
                  {viewingOrder.notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* Items Table */}
            <Divider orientation="left" style={{ fontSize: 14 }}>
              Artículos
            </Divider>
            <Table
              rowKey="id"
              columns={viewItemsColumns}
              dataSource={viewingOrder.items}
              size="small"
              pagination={false}
              scroll={{ x: isMobile ? 600 : undefined }}
            />

            {/* Totals */}
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: 'var(--bg-secondary)',
                borderRadius: 8,
                textAlign: 'right',
              }}
            >
              <div style={{ marginBottom: 4 }}>
                <Text type="secondary">Subtotal: </Text>
                <Text strong>{formatCurrency(viewingOrder.subtotal)}</Text>
              </div>
              <div style={{ marginBottom: 4 }}>
                <Text type="secondary">IVA: </Text>
                <Text strong>{formatCurrency(viewingOrder.tax)}</Text>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text type="secondary">Total: </Text>
                <Text strong style={{ fontSize: 18, color: 'var(--color-success-text)' }}>
                  {formatCurrency(viewingOrder.totalAmount)}
                </Text>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ==================== Receive Order Modal ==================== */}
      <Modal
        title={
          <Space>
            <CheckCircleOutlined />
            <span>Recibir Orden #{receivingOrder?.orderNumber}</span>
          </Space>
        }
        open={receiveModalOpen}
        onCancel={handleCloseReceive}
        onOk={handleSubmitReceive}
        okText="Registrar Recepción"
        cancelText="Cancelar"
        confirmLoading={receiving}
        width={isMobile ? '98%' : 700}
        style={{ top: isMobile ? 10 : 20 }}
        destroyOnClose
      >
        {receivingOrder && (
          <div>
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                background: 'var(--bg-info-light)',
                borderRadius: 8,
                border: '1px solid var(--border-info)',
              }}
            >
              <Text>
                Proveedor: <Text strong>{receivingOrder.supplierName || <Text type="secondary" italic>Sin proveedor</Text>}</Text>
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Ingresa la cantidad recibida para cada artículo. Los artículos con cantidad 0 serán
                ignorados.
              </Text>
            </div>

            <Table
              rowKey="purchaseOrderItemId"
              columns={receiveColumns}
              dataSource={receiveItems}
              size="small"
              pagination={false}
              scroll={{ x: isMobile ? 500 : undefined }}
            />

            <div style={{ marginTop: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                Notas de recepción:
              </Text>
              <TextArea
                rows={2}
                placeholder="Notas sobre la recepción (opcional)"
                value={receiveNotes}
                onChange={(e) => setReceiveNotes(e.target.value)}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* ==================== Low Stock Suggestions Modal ==================== */}
      <Modal
        title={
          <Space>
            <AlertOutlined />
            <span>Lista de Compras por Stock Bajo</span>
          </Space>
        }
        open={suggestionsModalOpen}
        onCancel={handleCloseSuggestions}
        footer={
          suggestionItems.length > 0 ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button onClick={handleCloseSuggestions}>Cancelar</Button>
              <Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Total: <Text strong>{formatCurrency(suggestionItems.reduce((sum, i) => sum + i.suggestedQuantity * i.unitPrice, 0))}</Text>
                </Text>
                <Button
                  type="primary"
                  loading={creatingFromSuggestions}
                  onClick={handleCreateFromSuggestions}
                >
                  Crear {new Set(suggestionItems.filter(i => i.suggestedQuantity > 0).map(i => i.selectedSupplierId ?? '__null__')).size} Orden(es)
                </Button>
              </Space>
            </div>
          ) : (
            <Button onClick={handleCloseSuggestions}>Cerrar</Button>
          )
        }
        width={isMobile ? '98%' : 1000}
        style={{ top: isMobile ? 10 : 20 }}
        destroyOnClose
      >
        {suggestionItems.length === 0 ? (
          <Empty description="No hay insumos con stock bajo" style={{ padding: '40px 0' }} />
        ) : (
          <div>
            {/* Bulk actions */}
            <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Proveedores:</Text>
              <Button size="small" onClick={handleApplySuggestedSuppliers}>
                Usar sugeridos
              </Button>
              <Button size="small" onClick={handleClearAllSuppliers}>
                Quitar todos
              </Button>
            </div>

            <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
              <Table
                rowKey="inventoryItemId"
                dataSource={suggestionItems}
                size="small"
                pagination={false}
                scroll={{ x: isMobile ? 700 : undefined }}
                columns={[
                  {
                    title: 'Insumo',
                    dataIndex: 'itemName',
                    key: 'itemName',
                    width: isMobile ? 110 : 160,
                    ellipsis: true,
                  },
                  {
                    title: 'Stock',
                    key: 'stock',
                    width: 90,
                    render: (_: unknown, record: SuggestionItem) => (
                      <Text type="danger" style={{ fontSize: 12 }}>{formatStock(record.currentStock)}/{record.minimumStock} {record.unit}</Text>
                    ),
                  },
                  {
                    title: 'Cant.',
                    dataIndex: 'suggestedQuantity',
                    key: 'suggestedQuantity',
                    width: 90,
                    render: (val: number, _record: SuggestionItem, index: number) => (
                      <InputNumber
                        size="small"
                        value={val}
                        min={0.01}
                        step={1}
                        style={{ width: 75 }}
                        onChange={(newVal) => handleSuggestionQtyChange(index, newVal)}
                      />
                    ),
                  },
                  {
                    title: 'Precio',
                    dataIndex: 'unitPrice',
                    key: 'unitPrice',
                    width: 80,
                    render: (val: number) => (
                      <Text style={val === 0 ? { color: '#fa8c16', fontSize: 12 } : { fontSize: 12 }}>
                        {formatCurrency(val)}
                      </Text>
                    ),
                  },
                  {
                    title: 'Proveedor',
                    key: 'supplier',
                    width: isMobile ? 130 : 180,
                    render: (_: unknown, record: SuggestionItem, index: number) => (
                      <Select
                        size="small"
                        value={record.selectedSupplierId ?? undefined}
                        onChange={(val: string | undefined) => handleSuggestionSupplierChange(index, val ?? null)}
                        allowClear
                        placeholder="Sin proveedor"
                        style={{ width: '100%' }}
                        popupMatchSelectWidth={false}
                      >
                        {suppliers?.filter(s => s.isActive).map(s => (
                          <Option key={s.id} value={s.id}>
                            {s.name}
                            {s.id === record.suggestedSupplierId && (
                              <Text type="secondary" style={{ fontSize: 10, marginLeft: 4 }}>(sugerido)</Text>
                            )}
                          </Option>
                        ))}
                      </Select>
                    ),
                  },
                  {
                    title: 'Total',
                    key: 'total',
                    width: 90,
                    render: (_: unknown, record: SuggestionItem) =>
                      <Text style={{ fontSize: 12 }}>{formatCurrency(record.suggestedQuantity * record.unitPrice)}</Text>,
                  },
                  {
                    title: '',
                    key: 'actions',
                    width: 36,
                    render: (_: unknown, _record: SuggestionItem, index: number) => (
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => handleRemoveSuggestionItem(index)}
                      />
                    ),
                  },
                ]}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PurchaseOrdersTab;
